import React from "react";
import { FocusOn } from "react-focus-on";
import { Box, IBoxProps, Sidebar, styledSystem as ss } from ".";
import { ISidebarProps } from "./Sidebar";

/**
 * The site layout settings
 */
export interface ISiteLayoutProps {
	/**
	 * If the sidebar should be open or not
	 */
	open: boolean;
	/**
	 * The header content
	 */
	header: React.ReactNode;
	/**
	 * The sidebar content
	 */
	sidebar: React.ReactNode;
	/**
	 * The main content
	 */
	children: React.ReactNode;
	/**
	 * The container width. It will match the
	 * theme sizes or render as is
	 */
	containerWidth: number | string;
	/**
	 * The header container width. It will match the
	 * theme sizes or render as is
	 */
	headerContainerWidth: number | string;
	/**
	 * The sidebar breakpoint
	 */
	breakpoint: number;
	/**
	 * The height of the header
	 */
	headerHeight: string;
	/**
	 * If the header should be sticky or not
	 */
	headerSticky: boolean;
	/**
	 * If the header should be inside
	 */
	headerInside: boolean;
	/**
	 * The direction where the hidebar hides to
	 */
	sidebarHidesTo: "left" | "right";
	/**
	 * If the sidebar should be over the header
	 */
	sidebarOverHeader: boolean;
	/**
	 * The position of the sidebar.
	 */
	sidebarPosition: "left" | "right";
	/**
	 * The event to perform when the backdrop is clicked.
	 * Normally it should change the open prop to false
	 */
	onBackdropClick: () => void;

	/**
	 * The class name to apply to the site layout container
	 */
	siteClassName?: string;
	/**
	 * The properties to apply to the site layout container
	 */
	siteProps?: IBoxProps;
	/**
	 * The class name to apply to the header left side
	 */
	headerLeftClass?: string;
	/**
	 * Properties to apply to the header left side
	 */
	headerLeftProps?: IBoxProps;
	/**
	 * The class name to apply to the header right side
	 */
	headerRightClass?: string;
	/**
	 * Properties to apply to the header right side
	 */
	headerRightProps?: IBoxProps;
	/**
	 * The class name to apply to the header wrapper class
	 */
	headerWrapperClass?: string;
	/**
	 * The properties to add to the header wrapper
	 */
	headerWrapperProps?: IBoxProps;
	/**
	 * The class name to apply to the header container class
	 */
	headerContainerClass?: string;
	/**
	 * The properties to apply to the header container class
	 */
	headerContainerProps?: IBoxProps;
	/**
	 * The class name to apply to the main wrapper
	 */
	mainWrapperClass?: string;
	/**
	 * The properties to apply to the main wrapper
	 */
	mainWrapperProps?: IBoxProps;
	/**
	 * The class name to apply to the main container
	 */
	mainContainerClass?: string;
	/**
	 * The properties to apply to the main container
	 */
	mainContainerProps?: IBoxProps;
	/**
	 * The class name to apply to the content wrapper
	 */
	contentWrapperClass?: string;
	/**
	 * The properties to apply to the content wrapper
	 */
	contentWrapperProps?: IBoxProps;
	/**
	 * The class name to apply to the sidebar left side
	 */
	sidebarLeftClass?: string;
	/**
	 * The properties to apply to the sidebar left side
	 */
	sidebarLeftProps?: IBoxProps;
	/**
	 * The class name to apply to the sidebar right side
	 */
	sidebarRightClass?: string;
	/**
	 * The properties to apply to the sidebar right side
	 */
	sidebarRightProps?: IBoxProps;
	/**
	 * The class name to apply to the sidebar wrapper class
	 */
	sidebarWrapperClass?: string;
	/**
	 * The properties to add to the sidebar wrapper
	 */
	sidebarWrapperProps?: IBoxProps;
	/**
	 * The class name to apply to the sidebar container class
	 */
	sidebarContainerClass?: string;
	/**
	 * The properties to add to the sidebar container
	 */
	sidebarProps?: IBoxProps;
	/**
	 * The class name to apply to the backdrop
	 */
	backdropClass?: string;
	/**
	 * The properties to apply to the backdrop
	 */
	backdropProps?: IBoxProps;
}


/**
 * The styled FocusOn component
 */
const StyledFocusOn = ss.styled(FocusOn)<ss.ILayoutProps>`
	${ss.LAYOUT}
`;

/**
 * The styled backdrop
 */
const StyledBackdrop = ss.styled(Box)`
	&& {
		pointer-events: all!important;
	}
`;

/**
 * The site layout header
 *
 * @param props
 */
const SiteLayoutHeader = (props: ISiteLayoutProps) => {
	const wrapperBoxProps: IBoxProps = {
		...props.headerWrapperProps,
		position: props.headerSticky ? "sticky" : undefined,
		top: props.headerSticky ? 0 : undefined,
		zIndex: props.headerSticky ? "sticky" : undefined,
		display: props.headerSticky ? "flex" : undefined,
		height: props.headerHeight,
	};

	const containerBoxProps: IBoxProps = {
		...props.headerContainerProps,
		width: "100%",
		maxWidth: props.headerContainerWidth,
		mx: props.headerContainerWidth !== undefined ? "auto": undefined,
	};

	// Render the header if a header is provided
	return props.header ? (
		<Box className={props.headerWrapperClass} {...wrapperBoxProps}>
			{ !props.headerInside && <Box className={props.headerLeftClass} {...props.headerLeftProps} flex={1} /> }
			<Box className={props.headerContainerClass} {...containerBoxProps}>
				{props.header}
			</Box>
			{ !props.headerInside && <Box className={props.headerRightClass} {...props.headerRightProps} flex={1} /> }
		</Box>
	) : null;
};

/**
 * The site layout sidebar
 *
 * @param props
 */
const SiteLayoutSidebar = (props: ISiteLayoutProps) => {
	// Properties for the wrapper
	const wrapperBoxProps: IBoxProps = {
		...props.sidebarWrapperProps,
		position: "sticky",
		zIndex: "sidebar",
		top: props.headerInside || props.sidebarOverHeader || !props.headerSticky ? "0" : props.headerHeight,
		height: props.headerInside || props.sidebarOverHeader || !props.headerSticky ? `100vh` : `calc(100vh - ${props.headerHeight})`,
		mt: props.sidebarOverHeader ? `-${props.headerHeight}` : undefined,
	};

	// Create the classname
	const sidebarProps: ISidebarProps = {
		...props.sidebarProps,
		open: props.open,
		fixed: true,
		hidesRight: props.sidebarHidesTo === "right",
		breakpoint: props.breakpoint,
	};

	// Render the box with a focus on when opened to prevent page scrolling
	return props.sidebar ? (
		<Box className={props.sidebarWrapperClass} {...wrapperBoxProps}>
			<Sidebar className={props.sidebarContainerClass} {...sidebarProps}>
				<StyledFocusOn enabled={props.open} returnFocus={true} height="100%" overflow="auto">
					{props.sidebar}
				</StyledFocusOn>
			</Sidebar>
		</Box>
	) : null;
};

/**
 * Render the Site layout
 *
 * @param partialProps
 */
export const SiteLayout = (partialProps: Partial<ISiteLayoutProps>) => {
	const props: ISiteLayoutProps = {
		open: false,
		containerWidth: "xl",
		breakpoint: 3,

		headerContainerWidth: partialProps.containerWidth === undefined ? "xl" : partialProps.containerWidth,
		headerHeight: "66px",
		headerSticky: true,
		headerInside: false,

		sidebarPosition: "left",
		sidebarHidesTo: "right",
		sidebarOverHeader: false,

		onBackdropClick: () => {},

		header: null,
		sidebar: null,
		children: null,

		...partialProps,
	};

	const mainContainerBoxProps: IBoxProps = {
		...props.mainContainerProps,
		display: "flex",
		flex: "1 1 auto",
		width: "100%",
		maxWidth: props.containerWidth,
		mx: props.containerWidth !== undefined ? "auto": undefined,
	};

	return (
		<Box className={props.siteClassName} {...props.siteProps} display="flex" flexDirection="column" minHeight="100vh">
			{ !props.headerInside && <SiteLayoutHeader {...props} /> }

			<Box className={props.mainWrapperClass} {...props.mainWrapperProps} display="flex" flex="1 1 auto">
				<Box className={props.sidebarLeftClass} {...props.sidebarLeftProps} flex={1} />
				<Box className={props.mainContainerClass} {...mainContainerBoxProps}>
					{props.sidebarPosition === "left" && <SiteLayoutSidebar {...props} />}
					<Box className={props.contentWrapperClass} flex="1 1 auto" minWidth="0" overflowX="hidden">
						{props.headerInside && <SiteLayoutHeader {...props} />}
						{props.children}
					</Box>
					{props.sidebarPosition === "right" && <SiteLayoutSidebar {...props} />}
				</Box>
				<Box className={props.sidebarRightClass} {...props.sidebarRightProps} flex={1} />
			</Box>

			{
				props.open && <StyledBackdrop opacity={0.5} bg="black" {...props.backdropProps} zIndex="sidebarbg" position="fixed" top={0} left={0} right={0} bottom={0} onClick={props.onBackdropClick}
					className={props.backdropClass}
				/>
			}
		</Box>
	);
}
